import { useState } from "react";
import { X, Loader2, Check, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactModal = ({ open, onClose, mode = "contact", user = null }) => {
  const isConcierge = mode === "concierge";
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [subject, setSubject] = useState(isConcierge ? "Concierge request" : "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Please enter a message"); return; }
    if (!isConcierge && !email.trim()) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      const url = isConcierge ? `${API}/concierge` : `${API}/contact`;
      const payload = isConcierge
        ? { subject, message }
        : { email, name, subject, message };
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Failed to send");
      }
      setSent(true);
    } catch (err) {
      toast.error(err.message || "Failed to send");
    }
    setLoading(false);
  };

  const close = () => {
    setSent(false);
    setMessage("");
    if (!isConcierge) setSubject("");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={close}>
      <div
        className="bg-[#f5f0e8] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${mode}-modal`}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#e8e0d0]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#1e3a5f] rounded-lg flex items-center justify-center">
              {isConcierge ? <Sparkles className="w-5 h-5 text-[#c9a962]" /> : <Mail className="w-5 h-5 text-[#c9a962]" />}
            </div>
            <h2 className="text-lg font-bold text-[#1e3a5f]">
              {isConcierge ? "Request Concierge Help" : "Contact Us"}
            </h2>
          </div>
          <button onClick={close} className="text-[#1e3a5f]/50 hover:text-[#1e3a5f] p-1 rounded-lg hover:bg-[#e8e0d0]" data-testid={`${mode}-close-btn`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">Message sent!</h3>
            <p className="text-[#1e3a5f]/60 mb-6">
              {isConcierge
                ? "Our team will reach out to you soon about your concierge request."
                : "Thanks for reaching out — the team will get back to you shortly."}
            </p>
            <button onClick={close} className="bg-[#1e3a5f] text-[#f5f0e6] px-5 py-2.5 rounded-lg font-medium hover:bg-[#2a4d6e]" data-testid={`${mode}-done-btn`}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            {isConcierge ? (
              <>
                <p className="text-sm text-[#1e3a5f]/70 bg-white rounded-lg p-3 border border-[#e8e0d0]">
                  Requesting as <span className="font-semibold text-[#1e3a5f]">{user?.name || user?.email}</span>. We'll use your profile to tailor the help.
                </p>
                <p className="text-xs text-[#8a6d1f] bg-[#f5efd8] border border-[#e6d9a8] rounded-lg p-3" data-testid="concierge-disclaimer">
                  Heads up: we're a small team working at limited capacity, so it may take us a little while to get back to you. This doesn't mean your request was lost or that the feature isn't working — we'll follow up as soon as we can. Thanks for your patience!
                </p>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Your email</label>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-2.5 border-2 border-[#e8e0d0] rounded-xl focus:border-[#1e3a5f] focus:outline-none bg-white"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Your name (optional)</label>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Founder"
                    className="w-full px-4 py-2.5 border-2 border-[#e8e0d0] rounded-xl focus:border-[#1e3a5f] focus:outline-none bg-white"
                    data-testid="contact-name-input"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">Subject</label>
              <input
                type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder={isConcierge ? "What kind of help?" : "How can we help?"}
                className="w-full px-4 py-2.5 border-2 border-[#e8e0d0] rounded-xl focus:border-[#1e3a5f] focus:outline-none bg-white"
                data-testid={`${mode}-subject-input`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1e3a5f] mb-1">
                {isConcierge ? "What do you need help with?" : "Message"}
              </label>
              <textarea
                required value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
                placeholder={isConcierge ? "Describe your goals, what you've tried, and how we can help…" : "Write your message…"}
                className="w-full px-4 py-2.5 border-2 border-[#e8e0d0] rounded-xl focus:border-[#1e3a5f] focus:outline-none resize-none bg-white"
                data-testid={`${mode}-message-input`}
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-[#1e3a5f] text-[#f5f0e6] py-3 rounded-xl font-semibold hover:bg-[#2a4d6e] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid={`${mode}-submit-btn`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isConcierge ? <Sparkles className="w-4 h-4" /> : <Mail className="w-4 h-4" />)}
              {isConcierge ? "Send Request" : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
